import {
  Edit,
  TabbedForm,
  FormTab,
  TextInput,
  NumberInput,
  ReferenceInput,
  SelectInput,
  ArrayInput,
  SimpleFormIterator,
  ReferenceArrayInput,
  AutocompleteArrayInput,
  required,
  minValue,
} from 'react-admin';
import { MediaInput } from '../../components/inputs';

export const ProductEditTabs = () => (
  <Edit>
    <TabbedForm>
      {/* Основная информация */}
      <FormTab label="resources.products.tabs.general">
        <TextInput source="id" disabled />
        <TextInput source="name" validate={[required()]} fullWidth />
        <TextInput source="slug" fullWidth helperText="Автоматически генерируется из названия, если не указан" />
        <TextInput source="sku" validate={[required()]} />
        <TextInput source="description" multiline rows={4} fullWidth />
        
        <ReferenceInput source="brandId" reference="brands">
          <SelectInput optionText="name" />
        </ReferenceInput>
        
        <ReferenceInput source="categoryId" reference="categories">
          <SelectInput optionText="name" validate={[required()]} />
        </ReferenceInput>
        
        <NumberInput 
          source="price" 
          validate={[required(), minValue(0)]} 
          step={0.01}
        />
        
        <NumberInput source="quantity" validate={[minValue(0)]} />
        <NumberInput source="weight" validate={[minValue(0)]} step={0.01} />
        
        {/* Dimensions */}
        <NumberInput source="dimensions.length" label="Длина (см)" validate={[minValue(0)]} step={0.1} />
        <NumberInput source="dimensions.width" label="Ширина (см)" validate={[minValue(0)]} step={0.1} />
        <NumberInput source="dimensions.height" label="Высота (см)" validate={[minValue(0)]} step={0.1} />
        <TextInput source="dimensions.unit" label="Единица измерения" defaultValue="cm" />
        
        <SelectInput 
          source="status" 
          choices={[
            { id: 'DRAFT', name: 'Черновик' },
            { id: 'ACTIVE', name: 'Активный' },
            { id: 'INACTIVE', name: 'Неактивный' },
            { id: 'ARCHIVED', name: 'Архивный' },
          ]}
        />
      </FormTab>

      {/* SEO */}
      <FormTab label="resources.products.tabs.seo">
        <TextInput source="metaTitle" fullWidth />
        <TextInput source="metaDescription" multiline rows={3} fullWidth />
        <TextInput source="metaKeywords" fullWidth helperText="Ключевые слова через запятую" />
      </FormTab>

      {/* Медиа */}
      <FormTab label="resources.products.tabs.media">
        <MediaInput 
          source="images" 
          label="Изображения продукта" 
          multiple={true}
          accept="image/*"
          helperText="Выберите несколько изображений для галереи продукта"
        />
        
        <MediaInput 
          source="catalogImages" 
          label="Изображения для каталога" 
          multiple={true}
          accept="image/*"
          helperText="Выберите изображения для отображения в каталоге"
        />
      </FormTab>

      {/* Характеристики и атрибуты */}
      <FormTab label="resources.products.tabs.attributes">
        {/* Характеристики */}
        <ArrayInput source="characteristics" label="Характеристики">
          <SimpleFormIterator>
            <ReferenceInput source="characteristicId" reference="characteristics">
              <SelectInput optionText="name" validate={[required()]} />
            </ReferenceInput>
            <TextInput source="value" label="Значение" validate={[required()]} fullWidth />
          </SimpleFormIterator>
        </ArrayInput>

        {/* Атрибуты */}
        <ArrayInput source="attributeValues" label="Значения атрибутов">
          <SimpleFormIterator>
            <ReferenceInput source="attributeId" reference="attributes">
              <SelectInput optionText="name" validate={[required()]} />
            </ReferenceInput>
            <TextInput source="value" label="Значение" validate={[required()]} fullWidth />
          </SimpleFormIterator>
        </ArrayInput>

        {/* Теги */}
        <ReferenceArrayInput source="tags" reference="tags">
          <AutocompleteArrayInput optionText="name" />
        </ReferenceArrayInput>

        {/* Рекомендуемые товары */}
        <ArrayInput source="featuredProducts" label="Рекомендуемые товары">
          <SimpleFormIterator>
            <ReferenceInput source="featuredProductId" reference="products">
              <SelectInput optionText="name" validate={[required()]} />
            </ReferenceInput>
            <NumberInput source="sortOrder" label="Порядок сортировки" defaultValue={0} />
          </SimpleFormIterator>
        </ArrayInput>
      </FormTab>

      {/* Документы */}
      <FormTab label="resources.products.tabs.documents">
        <ArrayInput source="documents" label="Документы продукта">
          <SimpleFormIterator>
            <TextInput source="name" label="Название документа" validate={[required()]} fullWidth />
            <MediaInput 
              source="mediaId" 
              label="Файл документа" 
              validate={[required()]} 
              helperText="Выберите файл документа"
            />
            <NumberInput source="sortOrder" label="Порядок сортировки" defaultValue={0} />
          </SimpleFormIterator>
        </ArrayInput>
      </FormTab>

      {/* Конфигуратор */}
      <FormTab label="resources.products.tabs.configurator">
        <ArrayInput source="configuratorGroups" label="Группы конфигуратора">
          <SimpleFormIterator>
            <TextInput source="name" label="Название группы" validate={[required()]} fullWidth />
            <TextInput source="description" label="Описание" multiline rows={2} fullWidth />
            <NumberInput source="sortOrder" label="Порядок сортировки" defaultValue={0} />
            
            <ArrayInput source="options" label="Опции">
              <SimpleFormIterator>
                <TextInput source="name" label="Название опции" validate={[required()]} fullWidth />
                <NumberInput source="price" label="Цена" validate={[required(), minValue(0)]} step={0.01} />
                <SelectInput 
                  source="type" 
                  label="Тип опции"
                  choices={[
                    { id: 'RADIO', name: 'Переключатель' },
                    { id: 'CHECKBOX', name: 'Флажок' },
                  ]}
                  defaultValue="RADIO"
                />
                <MediaInput 
                  source="image" 
                  label="Изображение опции" 
                  multiple={false}
                  accept="image/*"
                  helperText="Выберите изображение для этой опции"
                />
                <TextInput source="sku" label="SKU" />
                <NumberInput source="sortOrder" label="Порядок сортировки" defaultValue={0} />
                
                {/* Дочерние опции */}
                <ArrayInput source="children" label="Дочерние опции">
                  <SimpleFormIterator>
                    <TextInput source="name" label="Название" validate={[required()]} fullWidth />
                    <NumberInput source="price" label="Цена" validate={[required(), minValue(0)]} step={0.01} />
                    <SelectInput 
                      source="type" 
                      label="Тип"
                      choices={[
                        { id: 'RADIO', name: 'Переключатель' },
                        { id: 'CHECKBOX', name: 'Флажок' },
                      ]}
                      defaultValue="RADIO"
                    />
                    <MediaInput 
                      source="image" 
                      label="Изображение" 
                      multiple={false}
                      accept="image/*"
                    />
                    <TextInput source="sku" label="SKU" />
                    <NumberInput source="sortOrder" label="Порядок сортировки" defaultValue={0} />
                  </SimpleFormIterator>
                </ArrayInput>
              </SimpleFormIterator>
            </ArrayInput>
          </SimpleFormIterator>
        </ArrayInput>
      </FormTab>
    </TabbedForm>
  </Edit>
);
